import { layoutStars } from "./star_layout.js";

const CORE = "255,250,242";
const SIZE_SCALE = 0.75;
const TAU = Math.PI * 2;

export function starLook(star) {
  if (!star.active) return { dot: 0.3, cloud: 0, cloudScale: 1 };
  if (star.stage === "created") return { dot: 0, cloud: 0.55, cloudScale: 1.7 };
  if (star.stage === "started") return { dot: 0.44, cloud: 0.62, cloudScale: 1.05 };
  return { dot: 0.94, cloud: 0.4 * star.formation, cloudScale: 0.9 + 0.9 * star.formation };
}

export function attentionHalo(star, clock, phase, still) {
  if (!star.active || !star.attention) return null;
  if (star.stage === "lapsed") return { radius: 11, alpha: 0.24, core: 0.62, coreScale: 1.32, coreBlur: 1.9 };
  const pulse = still ? 0.5 : 0.5 - 0.5 * Math.cos((clock + phase) * TAU);
  if (star.stage === "created" || star.stage === "started") {
    return { radius: 14 + pulse * 2, alpha: 0.22 + pulse * 0.1, core: 0.65, coreScale: 1, coreBlur: 0 };
  }
  return {
    radius: 12 + pulse * 3,
    alpha: 0.3 + pulse * 0.16,
    core: 0.9,
    coreScale: 1.22 + pulse * 0.18,
    coreBlur: 1.05 + pulse * 1.05,
  };
}

export function placeProtocolStars(stars, { seed, width, height, insets }) {
  const positions = layoutStars(stars, { seed, width, height, horizontalDrift: 0, insets });
  return stars.map((star) => ({ ...star, ...positions.get(star.id), look: starLook(star) }));
}

function glow(ctx, x, y, radius, rgb, alpha, middle = 0.3) {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, `rgba(${rgb},${alpha})`);
  gradient.addColorStop(0.25, `rgba(${rgb},${alpha * middle})`);
  gradient.addColorStop(1, `rgba(${rgb},0)`);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, TAU);
  ctx.fill();
}

function glint(ctx, radius) {
  ctx.beginPath();
  ctx.moveTo(0, -1.1 * radius);
  ctx.quadraticCurveTo(0, 0, 0.9 * radius, 0);
  ctx.quadraticCurveTo(0, 0, 0, 1.1 * radius);
  ctx.quadraticCurveTo(0, 0, -0.9 * radius, 0);
  ctx.quadraticCurveTo(0, 0, 0, -1.1 * radius);
  ctx.fill();
}

export function paintProtocolStars(ctx, stars, palette, elapsedMs, still, dpr) {
  ctx.save();
  ctx.scale(dpr, dpr);
  const clock = elapsedMs / 3500;
  for (const star of stars) {
    ctx.save();
    ctx.translate(star.x, star.y);
    if (!star.active) {
      ctx.fillStyle = `rgba(${CORE},0.3)`;
      ctx.beginPath();
      ctx.arc(0, 0, 2.25 * SIZE_SCALE, 0, TAU);
      ctx.fill();
      ctx.restore();
      continue;
    }

    const { look } = star;
    const attention = attentionHalo(star, clock, (star.x + star.y) * 0.01, still);
    const rgb = attention ? palette.attention : palette.star;
    const angle = ((((star.x * 7 + star.y * 3) % 100) - 50) * Math.PI) / 180;
    if (look.cloud > 0) {
      const radius = 24 * SIZE_SCALE * look.cloudScale;
      ctx.save();
      ctx.rotate(angle);
      ctx.scale(1.2, 0.5);
      glow(ctx, -radius * 0.12, 0, radius, rgb, 0.24 * look.cloud);
      ctx.rotate((32 * Math.PI) / 180);
      glow(ctx, radius * 0.2, -radius * 0.1, radius * 0.68, rgb, 0.14 * look.cloud);
      ctx.restore();
    }
    ctx.save();
    if (attention || look.dot === 0) {
      ctx.rotate(angle);
      ctx.scale(1.2, 0.75);
    }
    glow(
      ctx,
      0,
      0,
      (attention?.radius ?? (look.dot > 0 ? 14 : 20)) * SIZE_SCALE,
      rgb,
      attention?.alpha ?? (look.dot > 0 ? 0.1 + look.dot * 0.12 : 0.28)
    );
    ctx.restore();

    if (look.dot > 0) {
      let alpha = attention?.core ?? look.dot;
      let radius = 3.2 * SIZE_SCALE * (attention?.coreScale ?? 1);
      if (!attention && star.stage !== "started" && !still) {
        const twinkle = 0.5 + 0.5 * Math.sin((clock * 2 + (star.x + star.y) * 0.01) * TAU);
        alpha *= 0.82 + 0.18 * twinkle;
        radius *= 0.94 + 0.1 * twinkle;
      }
      const blur = (attention?.coreBlur ?? 0) * SIZE_SCALE;
      if (blur > 0) {
        for (const [x, y] of [
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ]) {
          ctx.save();
          ctx.translate(x * blur, y * blur);
          ctx.fillStyle = `rgba(${CORE},${alpha * 0.15})`;
          glint(ctx, radius);
          ctx.restore();
        }
      }
      ctx.fillStyle = `rgba(${CORE},${alpha * (blur > 0 ? 0.4 : 1)})`;
      glint(ctx, radius);
    }
    ctx.restore();
  }
  ctx.restore();
}
